import { forTarget } from '@core/content'
import { gqlQuery } from '@core/gqlQuery'
import { retry } from 'rxjs/operators'
import { BehaviorSubject } from 'rxjs'
import { fetchWithRetry } from '@core/fetch'

export type ProductsObservable = BehaviorSubject<Record<string, any>>

export type ProductDataFromLink = {
  slug: string
  name: string
}

export interface ProductByPK {
  id: string
  name: string
  slug: string
  rechargeable: boolean
  bluetooth: boolean
  price: number
  inEar: boolean
  behindEar: boolean
  typeOfHearingLoss: string
  mainImage: string
  productVariantStripeText: string
  comparisonVariantStripeText: string
  ratingAverage: number
  ratingCount: number
  audioFileBackgroundNoise: string
  audioFileQuietTV: string
  audioFileMusicStreaming: string
  scorecard: Scorecard
}

export interface Scorecard {
  id: string
  hearingLossLevel: string
  careModel: string
  soundQuality: string
  comfort: string
  style: string
  battery: string
  connection: string
  dexterityLevel: string
  waterproofLevel: string
  appFeatures: string
  hearingLossLevelRichText: string
  careModelRichText: string
  soundQualityRichText: string
  comfortRichText: string
  styleRichText: string
  batteryRichText: string
  connectionRichText: string
  dexterityLevelRichText: string
  waterproofLevelRichText: string
  appFeaturesRichText: string
}


/**
 * setProductComparisonData
 *
 * @param event
 * @param products$
 */
export function setProductComparisonData (event: CustomEvent, products$: ProductsObservable) {
  event.preventDefault()

  const target = event.currentTarget! as HTMLLinkElement
  const dataset = forTarget<HTMLSpanElement>(target, 'product-information')!.dataset as ProductDataFromLink
  const currentValue = products$.getValue() as Record<string, ProductDataFromLink>

  if (currentValue[dataset.slug]) {
    delete currentValue[dataset.slug!]
    products$.next({ ...currentValue })
  } else {
    addProductComparisonData(dataset, products$)
  }
}

/**
 * addProductComparisonData
 *
 * adds a product to the comparison but limits it to only 4 products
 *
 * @param dataset
 * @param products$
 */
function addProductComparisonData (dataset: ProductDataFromLink, products$: ProductsObservable) {
  const currentValue = products$.getValue()

  getProductByPKRest(dataset.slug, 2).then((product) => {
    const values: ProductDataFromLink[] = Object.values(currentValue)
    const count = 4

    if (values.length === count) {
      values[count - 1] = product
    } else {
      values.push(product)
    }

    const results = values.reduce((acc: Record<string, any>, curr) => {
      acc[curr.slug] = curr
      return acc
    }, {})

    products$.next(results)
  })
}

/**
 * toggleProductSelection
 *
 * @param link
 * @param dataset
 * @param products$
 */
export function toggleProductSelection (productElement: HTMLElement, products$: ProductsObservable) {
  products$.subscribe((products) => {
    const img = forTarget<HTMLImageElement>(productElement, 'selection-toggle-image')!
    const dataset = productElement.querySelector<HTMLElement>('[data-slug]')?.dataset as Record<string, any>
    const link = productElement.querySelector('a[dk-action="compare-product:click"]')!
    const productName = productElement.querySelector<HTMLElement>('[data-name]')?.getAttribute('data-name')

    if (!img || !dataset || !link) return

    if (products[dataset?.slug]) {
      link.setAttribute('aria-label', `Remove ${productName} from comparison`)
    } else {
      link.setAttribute('aria-label', `Add ${productName} to comparison`)
    }

    img.src = products[dataset?.slug]
      ? 'https://uploads-ssl.webflow.com/624750193b887ddb06ea64ee/62630780111fd424ded47845_CheckMark.svg'
      : 'https://uploads-ssl.webflow.com/624750193b887ddb06ea64ee/626307808b572f730960e8f6_Plus.svg'
  })
}

/**
 * getProductByPKRest
 * 
 * fetches the product from the API and maps it to the ProductByPK interface
 *
 * @param pk - product slug
 * @param retries - number of retries
 * @returns ProductByPK
 */
export async function getProductByPKRest (pk: string, retries = 3) {
  const url = `${process.env.ANUI_DOMAIN}/api/records/product/${pk}`
  
  const response = await fetchWithRetry(url, {}, retries)
  return mapProductByPKRest(response)
}

/**
 * mapProductByPKRest
 *
 * maps the raw data from the API to the ProductByPK interface
 * @param raw
 * @returns ProductByPK
 */
export function mapProductByPKRest (raw: any): ProductByPK {
  const hearingLossSeverity: Record<string, string> = {
    '4d7a29a873210a4e60c9fe4372496c27': 'Mild',
    '7002ea6c9319df9fe7b712d5c5f69cd6': 'Moderate',
    'bc37c2ca33698b6d744d4bb15d9414d2': 'Severe',
    '569facee511fe176285d26b0fb979a8f': 'Profound',
  };

  return {
    id: raw.id,
    name: raw.name,
    slug: raw.slug,
    rechargeable: raw.rechargeable,
    bluetooth: raw.bluetooth,
    price: raw["default-sku"]?.price?.value ?? 0,
    inEar: raw['in-the-ear'],
    behindEar: raw['behind-the-ear'],
    typeOfHearingLoss: hearingLossSeverity[raw['hearing-loss-level']],
    mainImage: raw['default-sku']?.['main-image']?.url,
    ratingAverage: raw['average-rating'] ?? 3,
    ratingCount: raw['rating-count'] ?? 0,
    productVariantStripeText: raw['product-variant-stripe-text'],
    comparisonVariantStripeText: raw['comparison-variant-stripe-text'],
    audioFileBackgroundNoise: raw['audio-file-link-background-noise'] ?? '',
    audioFileQuietTV: raw['audio-file-link-quiet-tv'] ?? '',
    audioFileMusicStreaming: raw['audio-file-link-music-streaming'] ?? '',
    scorecard: mapScorecardByPKRest(raw['soundly-scorecards']?.[0] || {}),
  }
}

/**
 * mapScorecardByPKRest
 * 
 * maps the raw data from the API to the Scorecard interface
 *
 * @param raw
 * @returns Scorecard
 */
export function mapScorecardByPKRest (raw: any): Scorecard {
  return {
    id: raw?.id,
    hearingLossLevel: raw?.['hearing-loss-level-tags-text-field'] ?? 'N/A',
    careModel: raw?.['care-model-tags-slugs'] ?? 'N/A',
    soundQuality: raw?.['sound-quality-tags-slugs'] ?? 'N/A',
    comfort: raw?.['comfort-tags-text-field'] ?? 'N/A',
    style: raw?.['style-tags-slugs'] ?? 'N/A',
    battery: raw?.['battery-tags-slugs'] ?? 'N/A',
    connection: raw?.['connection-tags-text-field'] ?? 'N/A',
    dexterityLevel: raw?.['dexterity-tags-slugs'] ?? 'N/A',
    waterproofLevel: raw?.['additional-tags-text-field'] ?? 'N/A',
    appFeatures: raw?.['app-features-tags-text-field'] ?? 'N/A',
    hearingLossLevelRichText: raw?.['hearing-loss-level-rich-text'] ?? 'N/A',
    careModelRichText: raw?.['care-model-rich-text'] ?? 'N/A',
    soundQualityRichText: raw?.['sound-quality-rich-text'] ?? 'N/A',
    comfortRichText: raw?.['comfort-rich-text'] ?? 'N/A',
    styleRichText: raw?.['style-rich-text'] ?? 'N/A',
    batteryRichText: raw?.['battery-rich-text'] ?? 'N/A',
    connectionRichText: raw?.['connection-rich-text'] ?? 'N/A',
    dexterityLevelRichText: raw?.['dexterity-rich-text'] ?? 'N/A',
    waterproofLevelRichText: raw?.['additional-rich-text'] ?? 'N/A',
    appFeaturesRichText: raw?.['app-features-rich-text'] ?? 'N/A',
  }
}
